import React, { useState } from 'react';
import { useAdmin } from '../hooks/useAdmin';
import { InfoIcon } from './icons/Icons';
import { Modal, Button, Badge, Disclosure } from './ui';
import './IFCExportDialog.css';

/**
 * Backwards-compatible IFCExportDialog wrapper around the Modal primitive.
 *
 * Public props (unchanged):
 *   isOpen, onExport, onCancel, structureName
 *
 * Emits: onExport(source) — same signature as before.
 */
const IFCExportDialog = ({
  isOpen,
  onExport,
  onCancel,
  structureName = 'Structure',
}) => {
  const { isAdmin } = useAdmin();
  const [selectedSource, setSelectedSource] = useState('both');

  const handleExport = () => {
    onExport(selectedSource);
  };

  const sourceOptions = [
    {
      value: 'rooms',
      label: 'Rooms Collection',
      description: 'Export data from individual room documents',
    },
    {
      value: 'structure',
      label: 'Structure Data',
      description: 'Export data from structure.walls and structure.floors',
    },
    {
      value: 'both',
      label: 'Both Sources',
      description: 'Export combined data from both rooms and structure',
    },
  ];

  return (
    <Modal
      open={isOpen}
      onClose={onCancel}
      title="Export to IFC"
      size="md"
      actions={
        <>
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleExport}>
            Export IFC
          </Button>
        </>
      }
    >
      <div className="ifc-export-info">
        <InfoIcon
          width={20}
          height={20}
          color="currentColor"
          className="ifc-export-info-icon"
        />
        <p>
          IFC is an open-standard file format for exchanging Building
          Information Modeling (BIM) data between compatible software, such as
          Autodesk Revit. The exported files have 3D modeling information for
          the entire structure.
        </p>
      </div>

      <p className="ifc-export-dialog-subtitle">
        Exporting: <strong>{structureName}</strong>
      </p>

      {/* Admin-only data source selection. */}
      {isAdmin && (
        <div className="ifc-export-advanced">
          <Disclosure
            summary={
              <span className="ifc-export-advanced-summary">
                <span className="ifc-export-advanced-label">
                  Advanced Options
                </span>
                <Badge variant="default">Admin</Badge>
              </span>
            }
          >
            <p className="ifc-export-advanced-panel-help">
              Choose data source:
            </p>
            <div className="ifc-export-source-options">
              {sourceOptions.map((option) => (
                <label
                  key={option.value}
                  className={`ifc-export-source-option ${
                    selectedSource === option.value ? 'selected' : ''
                  }`}
                >
                  <input
                    type="radio"
                    name="exportSource"
                    value={option.value}
                    checked={selectedSource === option.value}
                    onChange={(e) => setSelectedSource(e.target.value)}
                  />
                  <div className="ifc-export-source-info">
                    <span className="ifc-export-source-label">
                      {option.label}
                    </span>
                    <span className="ifc-export-source-description">
                      {option.description}
                    </span>
                  </div>
                </label>
              ))}
            </div>
          </Disclosure>
        </div>
      )}
    </Modal>
  );
};

export default IFCExportDialog;
